import { useHeroInView } from './useHeroInView';
import styles from './hero.module.css';

/**
 * HospitalsHero
 * Ported from the Careerbook mockup's hero graphic. Animations
 * (arc draw-in, card rise-ins, progress fill) play once, the first
 * time this scrolls into view, via useHeroInView — not on mount.
 * All text/numbers below are sample data from the mockup; pass real
 * values as props.
 */
export function HospitalsHero({
  registerTitle = 'FY1 core teaching — Sepsis',
  registerSubtitle = 'Education Centre, Room 2 · 12:30',
  registerPct = 93,
  registerCount = '42 of 45 signed in',
  certCount = '38 certificates issued',
  aiSummary = 'Block 3 built. 2 clashes fixed.',
} = {}) {
  const { ref, playing } = useHeroInView();

  return (
    <div
      ref={ref}
      className={`${styles.heroRoot} ${playing ? styles.playing : ''}`}
      style={{ position: 'relative', height: 600 }}
    >
      <svg
        width="620"
        height="600"
        viewBox="0 0 620 600"
        fill="none"
        aria-hidden="true"
        style={{ position: 'absolute', inset: '0' }}
      >
        <path
          className={styles.draw}
          d="M520 120A250 250 0 1 0 520 480"
          stroke="#5AA9EE"
          strokeWidth="64"
          strokeLinecap="round"
        />
        <path
          className={styles.draw}
          d="M430 210A120 120 0 1 0 430 390"
          stroke="#1E7FD8"
          strokeWidth="44"
          strokeLinecap="round"
          style={{ animationDelay: '.25s' }}
        />
      </svg>
      <div
        role="img"
        aria-label="Illustration: educator leading F1 teaching"
        style={{
          position: 'absolute',
          left: 60,
          top: 30,
          width: 250,
          height: 300,
          borderRadius: '140px 140px 28px 28px',
          border: '6px solid #ffffff',
          overflow: 'hidden',
          background: '#EAF3FC',
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 300 360"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
          style={{ display: 'block' }}
        >
          <rect width="300" height="360" fill="#EAF3FC" />
          <rect x="20" y="30" width="200" height="120" rx="10" fill="#ffffff" />
          <polyline
            points="34,80 70,80 80,62 90,98 100,40 112,110 122,80 170,80 180,66 190,92 200,50 210,104 220,80 210,80"
            fill="none"
            stroke="#1E7FD8"
            strokeWidth="4"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          <rect x="36" y="124" width="90" height="8" rx="4" fill="#CFE0F5" />
          <g transform="translate(235 190) scale(1.25)">
            <path d="M-44 70 Q-44 6 0 4 Q44 6 44 70 Z" fill="#1F2544" />
            <rect x="-7" y="-14" width="14" height="20" rx="4" fill="#8D5524" />
            <circle cx="0" cy="-30" r="20" fill="#8D5524" />
            <path d="M-21 -30 A21 21 0 0 1 21 -30 Q21 -40 10 -44 Q0 -40 -8 -42 Q-18 -40 -21 -30 Z" fill="#1A1A1A" />
            <path d="M-10 6 L0 34 L10 6" stroke="#1E7FD8" strokeWidth="3" fill="none" />
            <rect x="-6" y="32" width="12" height="16" rx="2" fill="#ffffff" />
          </g>
          <path d="M205 205 L170 150" stroke="#1F2544" strokeWidth="12" strokeLinecap="round" />
          <circle cx="168" cy="147" r="7" fill="#8D5524" />
          <g transform="translate(60 330) scale(1.6)">
            <path d="M-44 70 Q-44 6 0 4 Q44 6 44 70 Z" fill="#5AA9EE" />
            <rect x="-7" y="-14" width="14" height="20" rx="4" fill="#F1C27D" />
            <circle cx="0" cy="-30" r="20" fill="#F1C27D" />
            <circle cx="0" cy="-31" r="21" fill="#6B4423" />
            <path d="M-20 -30 Q-24 0 -12 6 L12 6 Q24 0 20 -30 Z" fill="#6B4423" />
          </g>
          <g transform="translate(160 345) scale(1.6)">
            <path d="M-44 70 Q-44 6 0 4 Q44 6 44 70 Z" fill="#1668C4" />
            <rect x="-7" y="-14" width="14" height="20" rx="4" fill="#C68642" />
            <circle cx="0" cy="-30" r="20" fill="#C68642" />
            <circle cx="0" cy="-31" r="21" fill="#1A1A1A" />
          </g>
          <g transform="translate(260 335) scale(1.6)">
            <path d="M-44 70 Q-44 6 0 4 Q44 6 44 70 Z" fill="#9CC9F5" />
            <rect x="-7" y="-14" width="14" height="20" rx="4" fill="#E0AC69" />
            <circle cx="0" cy="-30" r="20" fill="#E0AC69" />
            <circle cx="0" cy="-31" r="21" fill="#3B2A1A" />
          </g>
        </svg>
      </div>
      <div
        role="img"
        aria-label="Illustration: learner signing in on phone"
        style={{
          position: 'absolute',
          left: 300,
          top: 250,
          width: 230,
          height: 290,
          borderRadius: '28px 28px 130px 130px',
          border: '6px solid #ffffff',
          overflow: 'hidden',
          background: '#EAF3FC',
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 300 360"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
          style={{ display: 'block' }}
        >
          <rect width="300" height="360" fill="#DCEAF9" />
          <g transform="translate(150 250) scale(2.3)">
            <path d="M-44 70 Q-44 6 0 4 Q44 6 44 70 Z" fill="#1668C4" />
            <rect x="-7" y="-14" width="14" height="20" rx="4" fill="#C68642" />
            <circle cx="0" cy="-30" r="20" fill="#C68642" />
            <path
              d="M-22 -28 A22 22 0 0 1 22 -28 L24 4 Q18 8 14 2 L14 -26 Q0 -40 -14 -26 L-14 2 Q-18 8 -24 4 Z"
              fill="#2B1B10"
            />
            <path d="M-10 6 L0 34 L10 6" stroke="#1E7FD8" strokeWidth="3" fill="none" />
            <rect x="-6" y="32" width="12" height="16" rx="2" fill="#ffffff" />
          </g>
          <rect x="150" y="190" width="120" height="200" rx="18" fill="#1F2544" />
          <rect x="158" y="202" width="104" height="176" rx="10" fill="#ffffff" />
          <rect x="172" y="222" width="26" height="26" fill="#1F2544" rx="2" />
          <rect x="222" y="222" width="26" height="26" fill="#1F2544" rx="2" />
          <rect x="172" y="268" width="26" height="26" fill="#1F2544" rx="2" />
          <rect x="206" y="256" width="8" height="8" fill="#1F2544" rx="2" />
          <rect x="220" y="270" width="8" height="8" fill="#1F2544" rx="2" />
          <rect x="234" y="256" width="8" height="8" fill="#1F2544" rx="2" />
          <rect x="240" y="282" width="8" height="8" fill="#1F2544" rx="2" />
          <rect x="206" y="284" width="10" height="10" fill="#1F2544" rx="2" />
          <rect x="172" y="312" width="76" height="30" rx="15" fill="#E3F4EA" />
          <path
            d="M186 327 l6 6 l12 -12"
            stroke="#1D6B3A"
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect x="210" y="323" width="28" height="7" rx="3" fill="#1D6B3A" />
          <ellipse cx="150" cy="330" rx="26" ry="18" fill="#C68642" />
        </svg>
      </div>
      <div
        className={styles.rise1}
        style={{
          position: 'absolute',
          right: '0',
          top: 40,
          width: 300,
          background: '#ffffff',
          borderRadius: 20,
          padding: 20,
          boxShadow: '0 24px 50px -22px rgba(31,37,68,.45)',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: '600',
              color: '#1461AE',
              background: '#EAF3FC',
              padding: '4px 10px',
              borderRadius: 999,
            }}
          >
            Live register
          </span>
          <span style={{ color: '#1668C4' }}>
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="4" y="4" width="6" height="6" rx="1" />
              <rect x="14" y="4" width="6" height="6" rx="1" />
              <rect x="4" y="14" width="6" height="6" rx="1" />
              <path d="M14 14h2v2h-2z" />
              <path d="M18 14h2" />
              <path d="M14 18h2v2" />
              <path d="M19 17v3" />
            </svg>
          </span>
        </div>
        <div style={{ fontSize: 17, fontWeight: '700' }}>{registerTitle}</div>
        <div style={{ fontSize: 14, color: '#5A6078' }}>{registerSubtitle}</div>
        <div style={{ height: 10, background: '#EEF1F5', borderRadius: 999, overflow: 'hidden' }}>
          <div
            className={styles.fill}
            style={{ width: `${registerPct}%`, height: '100%', background: '#1668C4', borderRadius: 999 }}
          ></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
          <span style={{ fontWeight: '700' }}>{registerCount}</span>
          <span style={{ color: '#5A6078' }}>Example data</span>
        </div>
      </div>
      <div
        className={styles.rise2}
        style={{
          position: 'absolute',
          left: '0',
          bottom: 20,
          width: 270,
          background: '#ffffff',
          borderRadius: 20,
          padding: 18,
          boxShadow: '0 24px 50px -22px rgba(31,37,68,.45)',
          display: 'flex',
          gap: 14,
          alignItems: 'center',
        }}
      >
        <span
          style={{
            flex: 'none',
            width: 46,
            height: 46,
            borderRadius: 14,
            background: '#FDF0E4',
            color: '#B85B12',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="9" r="5.5" />
            <path d="M8.5 13.5L7 21l5-2.5 5 2.5-1.5-7.5" />
          </svg>
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 15, fontWeight: '700' }}>{certCount}</span>
          <span style={{ fontSize: 13, color: '#5A6078' }}>Automatically, after feedback</span>
        </div>
      </div>
      <div
        className={styles.rise3}
        style={{
          position: 'absolute',
          right: 20,
          bottom: 70,
          width: 230,
          background: '#1F2544',
          color: '#ffffff',
          borderRadius: 20,
          padding: 18,
          boxShadow: '0 24px 50px -22px rgba(31,37,68,.6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#B9D8F7' }}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3v4" />
            <path d="M12 17v4" />
            <path d="M3 12h4" />
            <path d="M17 12h4" />
            <path d="M6 6l2.5 2.5" />
            <path d="M15.5 15.5L18 18" />
            <path d="M18 6l-2.5 2.5" />
            <path d="M8.5 15.5L6 18" />
          </svg>
          AI timetable
        </span>
        <span style={{ fontSize: 16, fontWeight: '700', lineHeight: '1.35' }}>{aiSummary}</span>
      </div>
    </div>
  );
}

export default HospitalsHero;
